/**
 * Icons on resources are strings: an emoji, or `lucide:<name>` for one of the
 * curated line icons below (curated so the bundle carries ~80, not 1500).
 * `<Icon>` renders either; `iconText` is for places that can only show text.
 */
import { createElement, type ReactNode } from 'react'
import {
  Bell,
  Bike,
  Book,
  Bookmark,
  Brain,
  Briefcase,
  Bug,
  Calendar,
  Camera,
  Car,
  Cat,
  CheckCircle2,
  Clock,
  Cloud,
  Code,
  Coffee,
  Cpu,
  CreditCard,
  Database,
  Dog,
  Dumbbell,
  FileText,
  Flag,
  Flame,
  FlaskConical,
  Folder,
  FolderKanban,
  Gamepad2,
  Gift,
  GitBranch,
  Globe,
  GraduationCap,
  Hammer,
  Headphones,
  Heart,
  Home,
  Inbox,
  Key,
  Laptop,
  Layers,
  Leaf,
  Lightbulb,
  ListChecks,
  Lock,
  Mail,
  MapPin,
  Medal,
  MessageCircle,
  Monitor,
  Moon,
  Music,
  Notebook,
  Package,
  Palette,
  PartyPopper,
  PenLine,
  Phone,
  PiggyBank,
  Pill,
  Plane,
  Rocket,
  Server,
  Settings,
  Shield,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Star,
  Stethoscope,
  Sun,
  Target,
  Terminal,
  Timer,
  TreePine,
  Trophy,
  Truck,
  Umbrella,
  User,
  Users,
  Utensils,
  Wallet,
  Wifi,
  Wrench,
  Zap,
  type LucideIcon,
} from 'lucide-react'

export const ICON_PREFIX = 'lucide:'

export const LINE_ICONS: Record<string, LucideIcon> = {
  home: Home,
  briefcase: Briefcase,
  code: Code,
  terminal: Terminal,
  bug: Bug,
  'git-branch': GitBranch,
  flask: FlaskConical,
  server: Server,
  database: Database,
  cpu: Cpu,
  shield: Shield,
  key: Key,
  lock: Lock,
  wifi: Wifi,
  rocket: Rocket,
  lightbulb: Lightbulb,
  brain: Brain,
  sparkles: Sparkles,
  target: Target,
  flag: Flag,
  star: Star,
  heart: Heart,
  flame: Flame,
  zap: Zap,
  bookmark: Bookmark,
  book: Book,
  notebook: Notebook,
  'file-text': FileText,
  'pen-line': PenLine,
  folder: Folder,
  'folder-kanban': FolderKanban,
  layers: Layers,
  'list-checks': ListChecks,
  'check-circle': CheckCircle2,
  inbox: Inbox,
  mail: Mail,
  phone: Phone,
  'message-circle': MessageCircle,
  users: Users,
  user: User,
  calendar: Calendar,
  clock: Clock,
  timer: Timer,
  bell: Bell,
  sun: Sun,
  moon: Moon,
  cloud: Cloud,
  umbrella: Umbrella,
  leaf: Leaf,
  'tree-pine': TreePine,
  dog: Dog,
  cat: Cat,
  coffee: Coffee,
  utensils: Utensils,
  'shopping-cart': ShoppingCart,
  gift: Gift,
  'party-popper': PartyPopper,
  trophy: Trophy,
  medal: Medal,
  music: Music,
  headphones: Headphones,
  camera: Camera,
  gamepad: Gamepad2,
  palette: Palette,
  car: Car,
  bike: Bike,
  plane: Plane,
  truck: Truck,
  'map-pin': MapPin,
  globe: Globe,
  dumbbell: Dumbbell,
  pill: Pill,
  stethoscope: Stethoscope,
  wallet: Wallet,
  'credit-card': CreditCard,
  'piggy-bank': PiggyBank,
  'graduation-cap': GraduationCap,
  wrench: Wrench,
  hammer: Hammer,
  package: Package,
  laptop: Laptop,
  monitor: Monitor,
  smartphone: Smartphone,
  settings: Settings,
}

export const isLineIcon = (v: string | undefined): v is string => !!v && v.startsWith(ICON_PREFIX)
export const lineIcon = (v: string | undefined): LucideIcon | undefined =>
  isLineIcon(v) ? LINE_ICONS[v.slice(ICON_PREFIX.length)] : undefined

/** Emoji or line icon; `fallback` when the value is empty. Renders nothing with neither. */
export const Icon = ({
  value,
  size = 16,
  className,
  fallback,
}: {
  value?: string
  size?: number
  className?: string
  fallback?: ReactNode
}) => {
  const L = lineIcon(value)
  // createElement rather than <L/>: the component is looked up, not created, per render
  if (L)
    return createElement(L, {
      size,
      className,
      'aria-hidden': true,
      style: { display: 'inline-block', verticalAlign: '-0.15em' },
    })
  if (value && !isLineIcon(value)) return <span className={className}>{value}</span>
  return fallback ? <span className={className}>{fallback}</span> : null
}

/** Text-only contexts (titles, hints): emoji pass through, line icons drop out. */
export const iconText = (value: string | undefined, fallback = ''): string =>
  value && !isLineIcon(value) ? value : fallback
